import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getArtists } from '../api';
import { BOARD_FETCH_LIMIT } from '../hooks/useBoardData';
import ArtistAvatar from '../components/ArtistAvatar';
import { artistPath } from '../artistPath';
import { buildSearchIndex, searchArtists } from './searchModel';
import './headerSearch.css';
import Loader from '../components/Loader';

const SUGGESTIONS = 5;

function SearchGlyph({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-3.5-3.5" />
    </svg>
  );
}

// Artist search in the header, left of the shop. Typing suggests the top
// artists (photo, place, Motion) - picking one opens their profile; Enter or
// "See all results" opens the leaderboard filtered by it (/leaderboard?q=).
// The roster is the cached /api/artists board payload, fetched the first
// time someone opens the search rather than on every page load.
// `compact` (phones): a search icon that opens a full-width field over the
// header instead of an always-visible box.
export default function HeaderSearch({ compact = false }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(!compact);
  const [focused, setFocused] = useState(false);
  const [value, setValue] = useState('');
  const [json, setJson] = useState(null);
  const [active, setActive] = useState(-1);
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const listId = useId();

  const load = () => {
    if (json) return;
    getArtists(BOARD_FETCH_LIMIT).then((j) => setJson(j || {})).catch(() => setJson({}));
  };

  const index = useMemo(() => (json ? buildSearchIndex(json) : []), [json]);
  const results = useMemo(() => searchArtists(index, value, SUGGESTIONS), [index, value]);
  const query = value.trim();
  const showList = focused && query.length > 0;

  useEffect(() => { setActive(-1); }, [value]);

  // Close on a click outside.
  useEffect(() => {
    if (!focused) return undefined;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setFocused(false);
        if (compact) setOpen(false);
      }
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [focused, compact]);

  // "/" focuses the box (desktop), unless you're already typing somewhere.
  useEffect(() => {
    if (compact) return undefined;
    const onKey = (e) => {
      const t = e.target;
      const typing = t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable);
      if (e.key === '/' && !typing && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [compact]);

  const finish = () => {
    setValue('');
    setFocused(false);
    inputRef.current?.blur();
    if (compact) setOpen(false);
  };
  const openArtist = (a) => { finish(); navigate(artistPath(a.name, a.key)); };
  const openAll = () => {
    if (!query) return;
    finish();
    navigate(`/leaderboard?q=${encodeURIComponent(query)}`);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, results.length));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (active >= 0 && active < results.length) openArtist(results[active]);
      else openAll();
    } else if (e.key === 'Escape') {
      if (value) setValue('');
      else finish();
    }
  };

  if (compact && !open) {
    return (
      <button
        type="button"
        className="nx-hs-icon"
        aria-label="Search artists"
        onClick={() => { setOpen(true); load(); setTimeout(() => inputRef.current?.focus(), 0); }}
      >
        <SearchGlyph size={18} />
      </button>
    );
  }

  return (
    <div ref={rootRef} className={`nx-hs${compact ? ' is-compact' : ''}`}>
      <label className="nx-hs-box">
        <SearchGlyph />
        <input
          ref={inputRef}
          type="search"
          value={value}
          placeholder="Search artists"
          aria-label="Search artists"
          role="combobox"
          aria-expanded={showList}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={showList && active >= 0 ? `${listId}-${active}` : undefined}
          onFocus={() => { setFocused(true); load(); }}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={onKeyDown}
        />
        {!compact && !focused && !value && <kbd className="nx-hs-kbd" aria-hidden="true">/</kbd>}
      </label>
      {compact && (
        <button type="button" className="nx-hs-close" onClick={finish} aria-label="Close search">Cancel</button>
      )}

      {showList && (
        <ul id={listId} role="listbox" className="nx-hs-list">
          {!json && <li className="nx-hs-note"><Loader size="inline" label="Loading artists…" /></li>}
          {json && results.length === 0 && <li className="nx-hs-note">No artists match “{query}”.</li>}
          {results.map((a, i) => (
            <li
              key={a.key}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={active === i}
              className={`nx-hs-item${active === i ? ' is-active' : ''}`}
              onPointerDown={(e) => e.preventDefault()}
              onClick={() => openArtist(a)}
              onMouseEnter={() => setActive(i)}
            >
              <ArtistAvatar name={a.name} photoURL={a.photoURL} size={32} />
              <span className="nx-hs-text">
                <span className="nx-hs-name">{a.name}</span>
                {a.place && <span className="nx-hs-place">{a.place}</span>}
              </span>
              {a.motion !== null && <span className="nx-hs-motion">{a.motion.toFixed(1)}</span>}
            </li>
          ))}
          <li
            id={`${listId}-${results.length}`}
            role="option"
            aria-selected={active === results.length}
            className={`nx-hs-all${active === results.length ? ' is-active' : ''}`}
            onPointerDown={(e) => e.preventDefault()}
            onClick={openAll}
            onMouseEnter={() => setActive(results.length)}
          >
            See all results for “{query}” on the leaderboard
          </li>
        </ul>
      )}
    </div>
  );
}
